import { useState, useEffect, useCallback } from 'react';
import { Button } from 'utils/Button';
import { galleryData } from 'data/Gallery';
import { Link, useParams, useLocation } from 'react-router-dom';
import { cn } from 'utils/cn';
import ClipLoader from 'react-spinners/ClipLoader';
import Breadcrumbs from 'components/Breadcrumbs';
import {
  FaChevronLeft,
  FaChevronRight,
  FaExpand,
  FaCompress,
  FaArrowUp,
  FaImages
} from 'react-icons/fa';
import { TiDelete } from 'react-icons/ti';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { useSwipeable } from 'react-swipeable';
import { useRef } from 'react';
import React, { Suspense } from 'react';
import { getImageUrl } from '../utils/imageOptimization';
import { getMergedGalleryData } from '../services/galleryService';

const banner =
  'https://res.cloudinary.com/dwoubdppz/image/upload/v1751597792/oak-and-stone-remodel/kitchen/kitchen13.webp';

export const Gallery = () => {
  const { subgallery } = useParams();
  const location = useLocation();
  const [gridLoading, setGridLoading] = useState({});
  const [modalLoading, setModalLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [, setIsMediumScreen] = useState(window.innerWidth >= 768);
  const [isZoomed, setIsZoomed] = useState(false);
  const [IMAGES_PER_PAGE, setImagesPerPage] = useState(9);
  const [currentPage, setCurrentPage] = useState(1);
  const gridRef = useRef(null);
  const [mergedGalleryData, setMergedGalleryData] = useState(null);
  const [loading, setLoading] = useState(true);



  const scrollToTop = () => {
    window.scrollTo({ top: 350, behavior: 'smooth' });
  };

  // Removed the redirect logic since it's now handled at the router level

  useEffect(() => {
    const handleResize = () => {
      setIsMediumScreen(window.innerWidth >= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get static gallery data
  const staticGalleryData = subgallery
    ? galleryData.find((item) => item.slug === subgallery)
    : null;

  // Use merged data (static + uploaded images)
  const selectedGallery = mergedGalleryData || staticGalleryData;

  const handleGridImageLoad = (index) => {
    setGridLoading((prev) => ({ ...prev, [index]: false }));
  };

  const handleModalImageLoad = () => {
    setModalLoading(false);
  };

  const openModal = (index) => {
    setSelectedImageIndex(index);
    setModalLoading(true); // Reset modal loading state
    setIsZoomed(false);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedImageIndex(null);
    setIsZoomed(false);
    // Restore body scroll when modal is closed
    document.body.style.overflow = 'auto';
  };

  // Clean up body style on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const navigateImage = useCallback(
    (direction) => {
      setModalLoading(true); // Reset modal loading state
      setIsZoomed(false);
      if (direction === 'prev') {
        if (selectedImageIndex > 0) {
          setSelectedImageIndex((prev) => prev - 1);
        } else {
          setModalLoading(false); // Don't show spinner if not moving
        }
      } else if (direction === 'next') {
        if (
          selectedGallery &&
          selectedImageIndex < selectedGallery.imageCollection.length - 1
        ) {
          setSelectedImageIndex((prev) => prev + 1);
        } else {
          setModalLoading(false); // Don't show spinner if not moving
        }
      }
    },
    [selectedImageIndex, selectedGallery]
  );

  // Handle swipe gestures for modal
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => navigateImage('next'),
    onSwipedRight: () => navigateImage('prev'),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true
  });

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeModal();
      if (event.key === 'ArrowLeft') navigateImage('prev');
      if (event.key === 'ArrowRight') navigateImage('next');
      if (event.key === 'z' && event.ctrlKey) {
        setIsZoomed((prev) => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedImageIndex, selectedGallery, navigateImage]);

  // Fetch and merge gallery data when subgallery changes
  useEffect(() => {
    const fetchMergedData = async () => {
      if (staticGalleryData && subgallery) {
        setLoading(true);
        try {
          const merged = await getMergedGalleryData(staticGalleryData, subgallery);
          setMergedGalleryData(merged);
        } catch (error) {
          console.error('Error fetching merged gallery data:', error);
          setMergedGalleryData(staticGalleryData);
        } finally {
          setLoading(false);
        }
      } else {
        setMergedGalleryData(staticGalleryData);
        setLoading(false);
      }
    };

    fetchMergedData();
  }, [staticGalleryData, subgallery]);

  // Reset to first page when gallery changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedGallery]);

  // Pagination logic
  const totalImages = selectedGallery?.imageCollection.length || 0;
  const totalPages = Math.ceil(totalImages / IMAGES_PER_PAGE);
  const startIdx = (currentPage - 1) * IMAGES_PER_PAGE;
  const endIdx = startIdx + IMAGES_PER_PAGE;
  const paginatedImages =
    selectedGallery?.imageCollection.slice(startIdx, endIdx) || [];

  // Keyboard navigation for pagination
  const paginationRef = useRef(null);
  useEffect(() => {
    const handlePaginationKey = (e) => {
      if (document.activeElement !== paginationRef.current) return;
      if (e.key === 'ArrowLeft' && currentPage > 1) {
        setCurrentPage((p) => Math.max(1, p - 1));
      } else if (e.key === 'ArrowRight' && currentPage < totalPages) {
        setCurrentPage((p) => Math.min(totalPages, p + 1));
      }
    };
    window.addEventListener('keydown', handlePaginationKey);
    return () => window.removeEventListener('keydown', handlePaginationKey);
  }, [currentPage, totalPages]);

  // Simple pagination: show all page numbers, no ellipses
  function getPaginationPages(current, total) {
    // For small numbers of pages, show all
    if (total <= 5) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    // For larger numbers, show a window around the current page
    const pages = [];
    const windowSize = window.innerWidth < 640 ? 2 : 3; // Smaller window on mobile

    // Always show first page
    pages.push(1);

    // Calculate start and end of the window
    let windowStart = Math.max(2, current - windowSize);
    let windowEnd = Math.min(total - 1, current + windowSize);

    // Adjust window to show more numbers if we're near the edges
    if (current <= windowSize + 2) {
      windowEnd = Math.min(total - 1, windowSize * 2 + 2);
    }
    if (current >= total - (windowSize + 1)) {
      windowStart = Math.max(2, total - (windowSize * 2 + 1));
    }

    // Add ellipsis and window numbers
    if (windowStart > 2) pages.push('...');
    for (let i = windowStart; i <= windowEnd; i++) {
      pages.push(i);
    }
    if (windowEnd < total - 1) pages.push('...');

    // Always show last page
    if (total > 1) pages.push(total);

    return pages;
  }
  const paginationPages = getPaginationPages(currentPage, totalPages);

  // Results range
  const showingFrom = totalImages === 0 ? 0 : startIdx + 1;
  const showingTo = Math.min(endIdx, totalImages);

  return (
    <>
      <Helmet>
        <title>
          Remodeling Portfolio | Kitchen & Bathroom Renovations | Oak & Stone
          Construction
        </title>
        <meta
          name="description"
          content="Explore our portfolio of stunning kitchen and bathroom remodels in Weber, Davis, and Box Elder counties. See our custom cabinets, high-end finishes, and expert craftsmanship. Get inspired for your next renovation!"
        />
        {/* Open Graph Meta Tags */}
        <meta
          property="og:title"
          content="Remodeling Portfolio | Kitchen & Bathroom Renovations | Oak & Stone Construction"
        />
        <meta
          property="og:description"
          content="Explore our portfolio of stunning kitchen and bathroom remodels in Weber, Davis, and Box Elder counties. See our custom cabinets, high-end finishes, and expert craftsmanship. Get inspired for your next renovation!"
        />
        <meta
          property="og:image"
          content={getImageUrl("https://res.cloudinary.com/dwoubdppz/image/upload/v1751597792/oak-and-stone-remodel/kitchen/kitchen13.webp", 'large')}
        />
        <meta
          property="og:url"
          content="https://oakandstoneremodel.com/gallery"
        />
        <meta property="og:type" content="website" />
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Remodeling Portfolio | Kitchen & Bathroom Renovations | Oak & Stone Construction"
        />
        <meta
          name="twitter:description"
          content="Explore our portfolio of stunning kitchen and bathroom remodels in Weber, Davis, and Box Elder counties. See our custom cabinets, high-end finishes, and expert craftsmanship. Get inspired for your next renovation!"
        />
        <meta
          name="twitter:image"
          content={getImageUrl("https://res.cloudinary.com/dwoubdppz/image/upload/v1751597792/oak-and-stone-remodel/kitchen/kitchen13.webp", 'large')}
        />
        <meta
          name="twitter:url"
          content="https://oakandstoneremodel.com/gallery"
        />
        <link rel="canonical" href="https://oakandstoneremodel.com/gallery" />
      </Helmet>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Remodeling Portfolio | Kitchen & Bathroom Renovations | Oak & Stone Construction',
          url: 'https://oakandstoneremodel.com/gallery',
          description:
            'Explore our portfolio of stunning kitchen and bathroom remodels in Weber, Davis, and Box Elder counties. See our custom cabinets, high-end finishes, and expert craftsmanship. Get inspired for your next renovation!'
        })}
      </script>

      {/* Portfolio Header */}
      <div className="h-[45vh] w-full relative">
        <div className="absolute top-0 left-0 z-10 w-full h-full">
          <div className="grid content-center w-full h-full">
            <h1 className="text-4xl text-center text-white">PORTFOLIO</h1>
            <div className="mx-auto my-6 w-60 h-1 bg-white"></div>
            <div className="mx-auto mt-6 space-x-6 w-max">
              <Button text="request consultation" to="/contact" />
            </div>
          </div>
        </div>
        <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-[35%]"></div>
        <img
                          src={getImageUrl(banner, 'large')}
          className="object-cover flex-shrink-0 w-full h-full bg-no-repeat"
          alt="a kitchen remodel built by oak and stone in ogden utah, featuring beautiful custom cabinets and marble countertops"
        />
        <Breadcrumbs pathname={location.pathname} />
      </div>

      {/* Text Banner */}
      <div className="p-6 w-full text-center">
        {selectedGallery ? (
          <div className="my-6 max-w-[1000px] mx-auto">
            <p className="text-3xl">{selectedGallery.title}</p>
            <p className="mt-5 text-lg opacity-80">{selectedGallery.text}</p>
            

          </div>
        ) : (
          <p className="text-sm">
            Explore our portfolio and view our amazing projects!
          </p>
        )}
      </div>

      {/* Portfolio Nav */}
      <div className="flex top-16 z-40 flex-wrap justify-center py-1.5 mb-4 w-full md:sticky bg-neutral-200">
        {galleryData.map((item) => (
          <Link
            key={item.slug}
            to={`/gallery/${item.slug}`}
            onClick={scrollToTop}
            className={cn(
              'px-4 py-2.5 duration-200 text-sm hover:text-blue-600',
              subgallery === item.slug ? 'text-blue-600 ' : ''
            )}>
            {item.title.toUpperCase()}
          </Link>
        ))}
      </div>

      {/* Images Grid */}
      {loading ? (
        <div className="flex justify-center items-center p-8">
          <ClipLoader size={50} color="#000" />
          <span className="ml-3 text-lg">Loading gallery images...</span>
        </div>
      ) : (
        <div ref={gridRef} className="grid gap-4 p-4 mx-auto md:grid-cols-3">
          {paginatedImages.map((image, index) => {
            const globalIndex = startIdx + index;
            return (
              <motion.div
                key={globalIndex}
                className="overflow-hidden relative cursor-pointer group"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => openModal(globalIndex)}>
                {gridLoading[globalIndex] && (
                  <div className="flex absolute inset-0 justify-center items-center bg-gray-100">
                    <ClipLoader size={40} color="#000" />
                  </div>
                )}
                <motion.img
                  src={getImageUrl(image, 'medium')}
                  alt={`Gallery ${globalIndex}`}
                  className={`object-cover w-full h-full transition-transform duration-300 ${
                    gridLoading[globalIndex] ? 'opacity-0' : 'opacity-100'
                  }`}
                  onLoad={() => handleGridImageLoad(globalIndex)}
                  whileHover={{ scale: 1.03 }}
                  loading="lazy"
                />
                <motion.div
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                  className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-30 opacity-0 transition-opacity">
                  <FaExpand className="text-2xl text-white" />
                </motion.div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Pagination Controls + Results Range + Images Per Page Selector */}
      <div className="sticky bottom-2 z-30">
        {totalPages > 1 && (
          <div className="flex flex-col gap-2 justify-center items-center my-3 md:my-6 md:flex-row">
            <div className="flex flex-wrap gap-2 md:gap-4 items-center p-2 md:p-4 rounded-lg shadow-lg backdrop-blur-sm bg-white/90 max-w-[95vw] md:flex-nowrap">
              {/* Images Per Page Selector - Hidden on Mobile */}
              <div className="hidden gap-2 items-center pr-4 w-auto border-r border-gray-200 md:flex">
                <label
                  htmlFor="imagesPerPage"
                  className="flex gap-2 items-center text-sm text-gray-600"
                  title="Select number of images to display per page">
                  <FaImages className="text-blue-500" />
                  <span>Per page:</span>
                </label>
                <select
                  id="imagesPerPage"
                  className="px-3 py-1.5 text-sm bg-white rounded-md border border-gray-200 transition-all cursor-pointer hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  value={IMAGES_PER_PAGE}
                  onChange={(e) => {
                    setImagesPerPage(Number(e.target.value));
                    setCurrentPage(1);
                    scrollToTop();
                  }}>
                  {[6, 9, 12, 18, 24].map((num) => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </select>
              </div>

              {/* Results Range */}
              <div className="text-center border-gray-200 md:text-left md:border-r md:pr-4">
                <span
                  className="text-xs text-gray-600 md:text-sm"
                  title={`Page ${currentPage} of ${totalPages}`}>
                  {showingFrom}–{showingTo} of {totalImages}
                </span>
              </div>

              {/* Pagination Navigation */}
              <nav
                className="flex gap-0.5 justify-center items-center md:gap-1"
                aria-label="Gallery Pagination"
                tabIndex={0}
                ref={paginationRef}
                role="navigation">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="p-1 bg-white rounded-md border border-gray-200 transition-all md:p-2 hover:border-blue-400 hover:bg-blue-50 disabled:opacity-50 disabled:hover:border-gray-200 disabled:hover:bg-white"
                  onClick={() => {
                    setCurrentPage((p) => Math.max(1, p - 1));
                    scrollToTop();
                  }}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                  title="Previous page">
                  <FaChevronLeft className="text-sm text-gray-600 md:text-base" />
                </motion.button>

                <div className="flex gap-0.5 justify-center items-center md:gap-1">
                  {paginationPages.map((page, idx) =>
                    page === '...' ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="w-4 text-xs text-center text-gray-400 select-none md:w-6 md:text-sm">
                        ···
                      </span>
                    ) : (
                      <motion.button
                        key={page}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className={cn(
                          'w-6 h-6 md:w-8 md:h-8 rounded-md border transition-all flex items-center justify-center text-xs md:text-sm',
                          page === currentPage
                            ? 'bg-primary text-white border-primary font-medium hover:bg-primary-hover'
                            : 'bg-white hover:border-blue-400 hover:bg-blue-50'
                        )}
                        onClick={() => {
                          setCurrentPage(page);
                          scrollToTop();
                        }}
                        aria-current={page === currentPage ? 'page' : undefined}
                        aria-label={`Go to page ${page}`}
                        title={`Go to page ${page}`}>
                        {page}
                      </motion.button>
                    )
                  )}
                </div>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="p-1 bg-white rounded-md border border-gray-200 transition-all md:p-2 hover:border-blue-400 hover:bg-blue-50 disabled:opacity-50 disabled:hover:border-gray-200 disabled:hover:bg-white"
                  onClick={() => {
                    setCurrentPage((p) => Math.min(totalPages, p + 1));
                    scrollToTop();
                  }}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                  title="Next page">
                  <FaChevronRight className="text-sm text-gray-600 md:text-base" />
                </motion.button>
              </nav>

              {/* Scroll to Top Button - Hidden on Mobile */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="hidden p-2 ml-4 bg-white rounded-md border border-gray-200 transition-all md:block hover:border-blue-400 hover:bg-blue-50"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                aria-label="Scroll to top"
                title="Scroll to top">
                <FaArrowUp className="text-gray-600" />
              </motion.button>
            </div>
          </div>
        )}
      </div>

      {/* Enhanced Image Modal - Now works on all screen sizes */}
      <AnimatePresence>
        {selectedImageIndex !== null && selectedGallery && (
          <Suspense fallback={<div>Loading...</div>}>
            <motion.div
              className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-90 backdrop-blur-sm"
              onClick={closeModal}
              {...swipeHandlers}>
              <motion.div
                className="relative max-w-[95vw] h-[98vh] p-2 flex items-center justify-center"
                onClick={(e) => e.stopPropagation()}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}>
                {/* Loading spinner */}
                {modalLoading && (
                  <div className="flex absolute inset-0 justify-center items-center">
                    <ClipLoader color="#fff" size={50} />
                  </div>
                )}

                {/* Image */}
                <motion.img
                  src={getImageUrl(selectedGallery.imageCollection[selectedImageIndex], 'large')}
                  alt={`Gallery image ${selectedImageIndex + 1}`}
                  className={`object-contain max-w-full max-h-full transition-all duration-300 ${
                    isZoomed ? 'scale-150 cursor-zoom-out' : 'cursor-zoom-in'
                  } ${modalLoading ? 'opacity-0' : 'opacity-100'}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsZoomed(!isZoomed);
                  }}
                  onLoad={handleModalImageLoad}
                />

                {/* Controls - Only show when image is loaded */}
                {!modalLoading && (
                  <>
                    {/* Controls */}
                    <div className="flex absolute top-4 right-4 items-center space-x-2">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="p-2 text-white bg-black bg-opacity-50 rounded-full hover:bg-opacity-80"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsZoomed(!isZoomed);
                        }}>
                        {isZoomed ? <FaCompress /> : <FaExpand />}
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="p-2 text-white bg-black bg-opacity-50 rounded-full hover:bg-opacity-80"
                        onClick={closeModal}>
                        <TiDelete className="text-2xl" />
                      </motion.button>
                    </div>

                    {/* Navigation buttons - Only show when not zoomed */}
                    {!isZoomed && (
                      <div
                        className="flex absolute inset-x-0 justify-between px-4 pointer-events-none"
                        style={{ top: '50%', transform: 'translateY(-50%)' }}>
                        {/* Left button or placeholder */}
                        {selectedImageIndex > 0 ? (
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className="p-4 text-white bg-black bg-opacity-50 rounded-full transition-all duration-200 pointer-events-auto hover:bg-opacity-80 hover:bg-black"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateImage('prev');
                            }}
                            aria-label="Previous image">
                            <FaChevronLeft size={24} />
                          </motion.button>
                        ) : (
                          <div className="invisible p-4" />
                        )}
                        {/* Right button or placeholder */}
                        {selectedImageIndex <
                        selectedGallery.imageCollection.length - 1 ? (
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className="p-4 text-white bg-black bg-opacity-50 rounded-full transition-all duration-200 pointer-events-auto hover:bg-opacity-80 hover:bg-black"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateImage('next');
                            }}
                            aria-label="Next image">
                            <FaChevronRight size={24} />
                          </motion.button>
                        ) : (
                          <div className="invisible p-4" />
                        )}
                      </div>
                    )}

                    {/* Image counter */}
                    <div className="absolute bottom-4 left-1/2 px-4 py-2 text-white bg-black bg-opacity-50 rounded-full -translate-x-1/2">
                      {selectedImageIndex + 1} /{' '}
                      {selectedGallery.imageCollection.length}
                    </div>
                  </>
                )}
              </motion.div>
            </motion.div>
          </Suspense>
        )}
      </AnimatePresence>

      {/* Lead user to contact page */}
      <div className="flex justify-center mt-6 mb-10 w-full">
        <Button text="request consultation" to="/contact" />
      </div>
    </>
  );
};
